import "./tickerClarosports.sass";
import { useCallback, useEffect, useState } from "react";
import { useTickerData } from "../../hooks/useTickerData";
import { useTickerBanners } from "../../hooks/useTickerBanners";
import { BANNER_MOVE, getReservedRightWidth } from "./TickerExpandedImage/tickerBannerUtils";
import TickerLogo from "./TickerLogo/TickerLogo";
import TickerContent from "./TickerContent/TickerContent";
import TickerExpandedImage from "./TickerExpandedImage/TickerExpandedImage";
import Loading from "../loading/loading";
import { pollInterval } from "./constants-ticker";

// Intervalo de recarga por defecto (15 min) cuando el servicio de banners no
// define `time_refresh`. Si lo define, se usa ese valor (`time_refresh` minutos).
const DEFAULT_TICKER_RELOAD_INTERVAL = 15 * 60 * 1000;

/**
 * Ticker principal de Claro Sports.
 *
 * Recarga la página completa de forma periódica (equivalente a pulsar F5) para
 * reiniciar por completo sus datos, animaciones y temporizadores.
 *
 * @returns {JSX.Element} Ticker con datos deportivos actualizados.
 */
export default function TickerClarosports() {
  return <TickerClarosportsContent />;
}

function TickerClarosportsContent() {
  const { tickerData, loading } = useTickerData({
    pollInterval,
    enablePolling: true
  });

  const { playlist: bannerPlaylist, timeRefreshMs } = useTickerBanners({
    pollInterval,
    enablePolling: true
  });

  // Recarga la página completa de forma periódica. El intervalo lo define el
  // servicio de banners mediante `time_refresh` (minutos); si no lo provee, se
  // usa el valor por defecto.
  useEffect(() => {
    const reloadInterval = timeRefreshMs || DEFAULT_TICKER_RELOAD_INTERVAL;
    const timer = window.setInterval(() => {
      window.location.reload();
    }, reloadInterval);

    return () => window.clearInterval(timer);
  }, [timeRefreshMs]);

  // Modo del banner visible; "expandir arriba" requiere que el contenedor
  // permita el desbordamiento vertical para que la imagen salga del alto del ticker.
  const [bannerMove, setBannerMove] = useState(null);
  const handleBannerMoveChange = useCallback((move) => setBannerMove(move), []);
  const allowOverflowUp = bannerMove === BANNER_MOVE.EXPAND_UP;

  // Ancho ocupado por los banners anclados a la derecha (fijo / arriba). El
  // carrusel del contenido reserva este espacio para no quedar oculto detrás.
  const reservedRightWidth = getReservedRightWidth(bannerPlaylist);

  const stateClasses = useTickerStateClasses(tickerData);
  const containerClasses = [
    "ticker-clarosports-container",
    stateClasses,
    allowOverflowUp ? "ticker-clarosports-container--allow-overflow-up" : "",
  ].filter(Boolean).join(" ");

  if (loading && tickerData.length === 0) {
    return (
      <div className="ticker-clarosports-container">
        <Loading />
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <TickerLogo />
      <TickerContent tickerData={tickerData} reservedRightWidth={reservedRightWidth} />
      <TickerExpandedImage
        playlist={bannerPlaylist}
        onMoveChange={handleBannerMoveChange}
      />
    </div>
  );
}

/**
 * Hook para determinar las clases de estado del ticker basadas en el contenido
 */
export function useTickerStateClasses(tickerData = []) {
  // FOOT, BASE (MLB) y AMFO usan la tarjeta alta con carrusel vertical de
  // resultados, por lo que requieren reservar la misma altura en el contenedor.
  const hasFootResults = Array.isArray(tickerData) && tickerData.some((entry) => {
    const sportCode = entry?.league?.sport_code?.toUpperCase();
    const results = entry?.results;
    const hasEvents = Array.isArray(results)
      ? results.length > 0
      : Boolean(results && typeof results === "object" && Object.keys(results).length);
    return (sportCode === "FOOT" || sportCode === "BASE" || sportCode === "AMFO") && hasEvents;
  });

  const hasResultCards = Array.isArray(tickerData) && tickerData.some((entry) => {
    const results = entry?.results;
    const upcoming = entry?.upcoming;
    const hasEvents = (Array.isArray(results)
      ? results.length > 0
      : Boolean(results && typeof results === "object" && Object.keys(results).length))
      || (Array.isArray(upcoming)
        ? upcoming.length > 0
        : Boolean(upcoming && typeof upcoming === "object" && Object.keys(upcoming).length));
    return hasEvents;
  });

  const classes = [];
  if (hasResultCards) classes.push("ticker-clarosports-container--has-result-cards");
  if (hasFootResults) classes.push("ticker-clarosports-container--has-foot-results");

  return classes.join(" ");
}
